import {call, put, select} from '@redux-saga/core/effects';
import api from 'api';
import ApiService from 'api/ApiService';
import {DialogTypes} from 'common/names';
import {ImportOpenPGPKeysDialogResult} from 'features/layout/DialogRoutes/dialogs/keys/ImportOpenPGPKeysDialog';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {pushErrorToast, pushSuccessToast} from 'features/notifications/redux/actions';
import i18n from 'i18n';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {Action} from 'redux/helpers';
import {licenseSelectors} from 'redux/license';
import {IAddablePgpKey} from 'types/AddablePgpKey';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {keyTypes} from '..';
import {checkKeyUse} from './checkKeyUse';
import {DiplomatKeyType} from 'types/diplomat/keys/DiplomatKeyType';
import {layoutActions, layoutSelectors} from 'features/layout/redux';
import {CERT_PROP_NAME, folderNames} from 'common/constants';
import {IDiplomatFolder} from 'types/DiplomatFolder';
import {CertDialogProps, CertFormType} from 'components/dialogs/CertDialog';

export type ImportPGPKeyPayload = {
   folder?: INav;
   secret?: boolean;
};
export function* importPgpKeySaga({payload}: Action<ImportPGPKeyPayload>) {
   try {
      const folder = payload?.folder;
      const secret = !!payload?.secret;
      // const data: {secret: boolean, filePath?: string, passphrase?: string} = {secret};
      const filePath: string = yield call(getCertText, {
         secret,
      });
      if (!filePath) {
         return;
      }
      // data.filePath = filePath;

      // let passphrase: string;
      // if (secret) {
      //    passphrase = yield call(getPassword, i18n.t('Passphrase'), i18n.t('Enter File Passphrase'));
      //    if (!passphrase) {
      //       return;
      //    }
      //    data.passphrase = passphrase
      // }

      const keys: IAddablePgpKey[] = yield call(
         asyncSagaConstructor(api.importPgpRead, keyTypes.IMPORT_PGP_READ, {
            getMethod: ApiService.postSecureJson,
            data: {
               [CERT_PROP_NAME]: filePath,
               secret,
            },
         })
      );

      const isFeatureSupported: ReturnType<
         typeof licenseSelectors.isFeatureSupported
      > = yield select(licenseSelectors.isFeatureSupported);

      if (!keys.length) {
         yield put(
            pushErrorToast(
               i18n.t('Key data has no {{keyType}} to import.', {
                  keyType: secret ? i18n.t('key pairs') : i18n.t('public keys'),
               })
            )
         );
      } else if (keys.length > 1 && !isFeatureSupported(DiplomatFeature.KEY_RING_IMPORTING)) {
         yield put(
            pushErrorToast(
               i18n.t('File \\{{filePath}}\\ contains more than one {{keyType}}.', {
                  filePath,
                  keyType: secret ? i18n.t('key pair') : i18n.t('public key'),
               })
            )
         );
      } else {
         const res: ICloseSubDialogAction<ImportOpenPGPKeysDialogResult> = yield call(
            openDialogWithResult,
            {
               id: DialogTypes.ImportOpenPGPKeysDialog.id,
               data: {
                  keys,
                  secret,
               },
            }
         );
         const importedKeys = res.payload?.data?.keys;

         if (!importedKeys?.length) {
            return; // yield put(pushErrorToast(''))
         }

         let defaultFolder = {} as IDiplomatFolder;
         if (!folder) {
            defaultFolder = (yield select(
               layoutSelectors.folderByName(
                  secret ? folderNames.KEY_PAIRS : folderNames.PUBLIC_KEYS
               )
            )) as IDiplomatFolder;
         }

         const numKeysAdded: number = yield call(
            asyncSagaConstructor(
               secret ? api.importPgpKeyPair : api.importPgpPublic,
               secret ? keyTypes.IMPORT_PGP_KEY_PAIR : keyTypes.IMPORT_PGP_PUBLIC,
               {
                  getMethod: ApiService.postSecureJson,
                  data: {
                     filepath: filePath,
                     folderID: folder?.id ?? defaultFolder?.id,
                     keys: importedKeys,
                  },
               }
            )
         );

         yield put(pushSuccessToast(i18n.t('Key(s) has been added')));
         yield put(layoutActions.loadTreeNav());

         yield call(checkKeyUse, DiplomatKeyType.PGP, numKeysAdded);
      }
   } catch (e) {
      console.error('Unhandler error', e);
   }
}

function* getCertText({secret}: {secret: boolean}) {
   try {
      const text = secret ? 'PGP Keypair' : 'PGP Public Key';
      const title = 'Paste ' + text;

      const dbFilePathAction: ICloseSubDialogAction<CertFormType> = yield call(
         openDialogWithResult,
         {
            id: DialogTypes.CertDialog.id,
            data: {
               title,
               label: text + ' Body',
               info: i18n.t('Paste the contents of the key file below'),
               // filesOfType: secret ? SECRET_FILE_TYPES : PUBLIC_FILE_TYPES,
               // initialPath: exportDir.path + FILE_SEPARATOR,
               // directoriesOnly: false,
            } as CertDialogProps['data'],
         }
      );

      return dbFilePathAction.payload?.data?.body;
   } catch (e) {
      yield call(unhandedSagaError, e as Error);
   }
}
